import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Target,
  Eye,
  Heart,
  Lightbulb,
  Users,
  CheckCircle,
  ArrowRight,
  Linkedin,
  Sparkles,
  Building2,
  Award,
  Zap,
} from "lucide-react";

const About = () => {
  const values = [
    {
      icon: Lightbulb,
      title: "Innovation First",
      description: "Pushing boundaries for real hiring challenges with intelligent automation.",
    },
    {
      icon: Heart,
      title: "Human-Centric",
      description: "Technology should enhance, not replace human decisions.",
    },
    {
      icon: CheckCircle,
      title: "Quality & Accuracy",
      description: "Ensuring fairness and high standards in every assessment.",
    },
    {
      icon: Users,
      title: "Inclusive Growth",
      description: "Reducing bias and creating equal opportunities.",
    }
  ];

  const timeline = [
    {
      year: "2025",
      title: "Founded with a Mission",
      description: "Founded with a mission to close the hiring gap and transform recruitment through intelligent automation.",
    },
    {
      year: "2025",
      title: "MVP Development",
      description: "MVP development with intelligent skill verification and recruiter dashboards.",
    },
    {
      year: "Sep 2025",
      title: "Pilot Launch",
      description: "Pilot launch with early adopters (HRs & candidates).",
    },
    {
      year: "2026 (Goal)",
      title: "Global Expansion",
      description: "Expand to enterprise customers and scale globally.",
    }
  ];

  const founder = {
    name: "Sathya Naga Sai Vakacharla",
    role: "Founder & CEO",
    image: "/Sathya.jpeg",
    linkedin: "https://www.linkedin.com/in/sathya-naga-sai-vakacharla",
    bio: "Visionary entrepreneur and technology leader passionate about transforming the recruitment industry through intelligent automation. With a deep understanding of both technical innovation and business strategy, Sathya founded VetriaAI to bridge the gap between talented candidates and forward-thinking companies.",
    expertise: [
      "Product Strategy & Development",
      "AI & Machine Learning Applications",
      "SaaS Platform Architecture",
      "Business Development & Growth"
    ],
    vision: "I believe that every candidate deserves a fair opportunity to showcase their true potential, and every company should have access to the best talent. VetriaAI is my commitment to making hiring more efficient, unbiased, and human-centric.",
    achievements: [
      "Led the development and launch of VetriaAI's MVP",
      "Architected intelligent skill verification system",
      "Established strategic partnerships with early adopters"
    ]
  };

  const stats = [
    {
      icon: Building2,
      value: "500+",
      label: "Companies Trust Us",
      description: "Early adopters using our platform"
    },
    {
      icon: Users,
      value: "10K+",
      label: "Candidates Screened",
      description: "Successfully assessed and verified"
    },
    {
      icon: Zap,
      value: "80%",
      label: "Time Reduction",
      description: "Faster hiring process"
    },
    {
      icon: Award,
      value: "95%",
      label: "Accuracy Rate",
      description: "Assessment precision"
    }
  ];

  // Animation refs
  const heroRef = useRef(null);
  const aboutRef = useRef(null);
  const missionRef = useRef(null);
  const valuesRef = useRef(null);
  const timelineRef = useRef(null);
  const founderRef = useRef(null);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observers = [];
    const refs = [heroRef, aboutRef, missionRef, valuesRef, timelineRef, founderRef];

    refs.forEach((ref) => {
      if (!ref.current) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animated');
          }
        },
        { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
      );

      observer.observe(ref.current);
      observers.push(observer);
    });

    return () => {
      observers.forEach((obs) => obs.disconnect());
    };
  }, []);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Modern Navigation - Simplified */}
      <nav className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-0 -ml-1 group">
            <img src="/VetriaAI.png" alt="VetriaAI" className="h-8 w-8 sm:h-9 sm:w-9" />
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary via-red-700 to-primary bg-clip-text text-transparent group-hover:from-red-800 group-hover:via-primary group-hover:to-red-800 transition-all duration-300">
              VetriaAI
            </span>
          </Link>
          <Link
            to="/login"
            className="bg-primary text-white px-4 sm:px-6 py-2 sm:py-2.5 rounded-button hover:bg-primary-hover transition-all duration-300 font-semibold text-sm sm:text-base shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
          >
            Sign In
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-12 sm:py-16 md:py-24 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-50 via-white to-blue-50 opacity-50"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(185,28,28,0.1),transparent_50%)]"></div>

        <div ref={heroRef} className="relative max-w-4xl mx-auto text-center animate-on-scroll">
          <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-sm border border-gray-200 mb-6 sm:mb-8">
            <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary flex-shrink-0" />
            <span className="text-xs sm:text-sm font-semibold text-gray-700">About Us</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight px-2">
            About VetriaAI
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-8 px-4 max-w-3xl mx-auto">
            Transforming the future of hiring with intelligent automation. We're building the next generation of recruitment technology that makes hiring faster, fairer, and more efficient.
          </p>
        </div>
      </section>

      {/* About Section - Enhanced */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-white to-gray-50 px-4 sm:px-6">
        <div ref={aboutRef} className="max-w-7xl mx-auto animate-on-scroll">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div>
              <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full mb-4 sm:mb-6">
                <span className="text-xs sm:text-sm font-semibold text-primary">Who We Are</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                Revolutionizing Recruitment Through Technology
              </h2>
              <p className="text-base sm:text-lg text-gray-600 mb-4 leading-relaxed">
                VetriaAI is a cutting-edge SaaS platform designed to transform how companies hire and how candidates showcase their skills. We combine intelligent automation with human insight to create a more efficient, unbiased, and data-driven recruitment process.
              </p>
              <p className="text-base sm:text-lg text-gray-600 mb-4 leading-relaxed">
                Our platform empowers HR teams and recruiters to make better hiring decisions faster, while giving job seekers a fair opportunity to demonstrate their true capabilities through comprehensive skill assessments.
              </p>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                Built on modern cloud infrastructure with enterprise-grade security, VetriaAI is trusted by forward-thinking companies looking to modernize their hiring processes.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:gap-6">
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div key={index} className="bg-white p-6 rounded-card shadow-card hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-primary/20 text-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/5 rounded-card flex items-center justify-center mx-auto mb-3">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <div className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary to-red-700 bg-clip-text text-transparent mb-1">
                      {stat.value}
                    </div>
                    <div className="text-sm sm:text-base font-semibold text-gray-900 mb-1">
                      {stat.label}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600">
                      {stat.description}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6">
        <div ref={missionRef} className="max-w-7xl mx-auto animate-on-scroll">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full mb-3 sm:mb-4">
              <span className="text-xs sm:text-sm font-semibold text-primary">Our Purpose</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
              Mission & Vision
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 md:gap-12">
            <div className="group bg-white p-6 sm:p-8 rounded-card shadow-card hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-primary/20">
              <div className="flex items-center mb-4 sm:mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/5 rounded-card flex items-center justify-center mr-4">
                  <Target className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Our Mission</h2>
              </div>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                To democratize access to top talent by making the hiring process more efficient,
                unbiased, and data-driven through intelligent automation. We believe that every
                candidate deserves a fair chance to showcase their skills, and every company
                deserves access to the best talent.
              </p>
            </div>
            <div className="group bg-white p-6 sm:p-8 rounded-card shadow-card hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-primary/20">
              <div className="flex items-center mb-4 sm:mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/5 rounded-card flex items-center justify-center mr-4">
                  <Eye className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Our Vision</h2>
              </div>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                A world where hiring decisions are based on skills, merit, and potential—free
                from bias or superficial judgments. We envision a future where technology and
                human expertise work together to create more inclusive, efficient, and successful
                hiring outcomes for everyone.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-gray-50 via-white to-gray-50 px-4 sm:px-6">
        <div ref={valuesRef} className="max-w-7xl mx-auto animate-on-scroll">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full mb-3 sm:mb-4">
              <span className="text-xs sm:text-sm font-semibold text-primary">Our Values</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
              Core Values
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 px-4">
              The principles that guide everything we do
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="group bg-white p-6 rounded-card shadow-card hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-primary/20 hover:-translate-y-2 text-center animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-primary/10 to-primary/5 rounded-card flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">{value.title}</h3>
                  <p className="text-sm sm:text-base text-gray-600">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Journey */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6">
        <div ref={timelineRef} className="max-w-4xl mx-auto animate-on-scroll">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full mb-3 sm:mb-4">
              <span className="text-xs sm:text-sm font-semibold text-primary">Our Journey</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
              Our Journey
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 px-4">
              From inception to global impact
            </p>
          </div>
          <div className="space-y-6 sm:space-y-8">
            {timeline.map((item, index) => (
              <div
                key={index}
                className="flex flex-col sm:flex-row items-start gap-4 sm:gap-0 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="flex-shrink-0 w-full sm:w-24">
                  <div className="bg-primary text-white px-3 sm:px-4 py-2 rounded-button text-xs sm:text-sm font-semibold inline-block">
                    {item.year}
                  </div>
                </div>
                <div className="sm:ml-8 bg-white p-5 sm:p-6 rounded-card shadow-card border border-gray-100 flex-1 hover:shadow-xl transition-all duration-300">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-sm sm:text-base text-gray-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet Our Founder */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-gray-50 via-white to-gray-50 px-4 sm:px-6">
        <div ref={founderRef} className="max-w-5xl mx-auto animate-on-scroll">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full mb-3 sm:mb-4">
              <span className="text-xs sm:text-sm font-semibold text-primary">Leadership</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
              Meet Our Founder
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 px-4">
              The visionary behind VetriaAI's mission to transform recruitment
            </p>
          </div>

          <div className="bg-white rounded-card shadow-card border border-gray-100 overflow-hidden animate-scale-in">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 p-6 sm:p-8 md:p-10">
              {/* Founder Image & Basic Info */}
              <div className="lg:col-span-1 text-center lg:text-left">
                <div className="relative inline-block mb-4 sm:mb-6">
                  <img
                    src={founder.image}
                    alt={founder.name}
                    className="w-32 h-32 sm:w-40 sm:h-40 rounded-full mx-auto lg:mx-0 object-cover border-4 border-primary/20 shadow-lg"
                  />
                  <a
                    href={founder.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute bottom-0 right-0 bg-primary text-white p-3 rounded-full shadow-lg hover:bg-primary-hover transition-all duration-300 hover:scale-110"
                    aria-label="Connect on LinkedIn"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                  {founder.name}
                </h3>
                <p className="text-primary font-semibold text-base sm:text-lg mb-4">
                  {founder.role}
                </p>
                <a
                  href={founder.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm text-gray-600 hover:text-primary transition-colors"
                >
                  <Linkedin className="w-4 h-4 mr-2" />
                  Connect on LinkedIn
                </a>
              </div>

              {/* Founder Details */}
              <div className="lg:col-span-2 space-y-6">
                {/* Bio */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">About</h4>
                  <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                    {founder.bio}
                  </p>
                </div>

                {/* Vision/Quote */}
                <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-card p-4 sm:p-6 border-l-4 border-primary">
                  <p className="text-gray-700 italic text-sm sm:text-base leading-relaxed">
                    "{founder.vision}"
                  </p>
                </div>

                {/* Expertise */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Areas of Expertise</h4>
                  <div className="flex flex-wrap gap-2">
                    {founder.expertise.map((skill, index) => (
                      <span
                        key={index}
                        className="px-3 py-1.5 bg-primary/10 text-primary rounded-full text-xs sm:text-sm font-medium"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Key Achievements */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Key Achievements</h4>
                  <ul className="space-y-2">
                    {founder.achievements.map((achievement, index) => (
                      <li key={index} className="flex items-start text-gray-600 text-sm sm:text-base">
                        <CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                        <span>{achievement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-12 sm:py-16 md:py-20 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-red-800 to-red-900"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(255,255,255,0.1),transparent_50%)]"></div>

        <div className="relative max-w-4xl mx-auto text-center text-white">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 px-2">Join Our Mission</h2>
          <p className="text-base sm:text-lg md:text-xl mb-8 opacity-95 px-4">
            Ready to be part of the hiring revolution? Let's transform recruitment together.
          </p>
          <Link
            to="/contact"
            className="group bg-white text-primary px-6 sm:px-8 py-3 sm:py-4 rounded-button hover:bg-gray-50 transition-all duration-300 font-semibold text-base sm:text-lg inline-flex items-center shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
          >
            Get in Touch
            <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 sm:py-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-6 sm:gap-8 mb-8 sm:mb-12">
            <div className="md:col-span-1">
              <Link to="/" className="mb-4 inline-flex items-center gap-0 -ml-1">
                <img src="/VetriaAI.png" alt="VetriaAI" className="h-8 w-8" />
                <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary via-red-400 to-primary bg-clip-text text-transparent">
                  VetriaAI
                </span>
              </Link>
              <p className="text-gray-400 text-sm sm:text-base leading-relaxed">
                Revolutionizing recruitment with intelligent automation.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-primary">Product</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/features" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm sm:text-base">
                    Features
                  </Link>
                </li>
                <li>
                  <Link to="/security" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm sm:text-base">
                    Security
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-primary">Company</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/about" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm sm:text-base">
                    About
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm sm:text-base">
                    Contact
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-primary">Support</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/help-center" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm sm:text-base">
                    Help Center
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-primary">Legal</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/cancellations-refunds" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm sm:text-base">
                    Cancellations &amp; Refunds
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm sm:text-base">
                    Terms &amp; Conditions
                  </Link>
                </li>
                <li>
                  <Link to="/shipping" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm sm:text-base">
                    Digital Delivery
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm sm:text-base">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p className="text-sm sm:text-base">&copy; 2025 VetriaAI. All copyrights are reserved by VetriaAI.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;
